import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

/** The ten app translation languages. German is the source language. */
export const WEBSITE_LANGUAGES = [
  { key: "en", nativeName: "English", germanName: "Englisch", speech: "en-US" },
  { key: "ta", nativeName: "தமிழ்", germanName: "Tamilisch", speech: "ta-IN" },
  { key: "si", nativeName: "සිංහල", germanName: "Singhalesisch", speech: "si-LK" },
  { key: "hi", nativeName: "हिन्दी", germanName: "Hindi", speech: "hi-IN" },
  { key: "ar", nativeName: "العربية", germanName: "Arabisch", speech: "ar-SA" },
  { key: "tr", nativeName: "Türkçe", germanName: "Türkisch", speech: "tr-TR" },
  { key: "ru", nativeName: "Русский", germanName: "Russisch", speech: "ru-RU" },
  { key: "zh", nativeName: "中文", germanName: "Chinesisch", speech: "zh-CN" },
  { key: "fr", nativeName: "Français", germanName: "Französisch", speech: "fr-FR" },
  { key: "es", nativeName: "Español", germanName: "Spanisch", speech: "es-ES" },
] as const;

export type TranslationLanguage = (typeof WEBSITE_LANGUAGES)[number]["key"];
/** Preserve the legacy 'de' value used in some existing bilingual components. */
export type LanguageKey = TranslationLanguage | "de";

export const getWebsiteLanguage = (key: LanguageKey) =>
  WEBSITE_LANGUAGES.find(item => item.key === key) ?? WEBSITE_LANGUAGES[0];

const STORAGE_KEY = "selectedLanguage";
const validLanguage = (value: unknown): value is LanguageKey =>
  value === "de" || WEBSITE_LANGUAGES.some(item => item.key === value);

const initialLanguage = (): LanguageKey => {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "de" ? "en" : validLanguage(stored) ? stored : "en";
  } catch {
    return "en";
  }
};

type LanguageContextType = {
  language: LanguageKey;
  setLanguage: (lang: LanguageKey) => void;
};
const LanguageContext = createContext<LanguageContextType>({
  language: "en",
  setLanguage: () => undefined,
});
export const useLanguage = () => useContext(LanguageContext);
export const LanguageProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [language, setLanguageState] = useState<LanguageKey>(initialLanguage);
  const setLanguage = useCallback((nextLanguage: LanguageKey): void => {
    if (!validLanguage(nextLanguage)) return;
    setLanguageState(nextLanguage);
    try { window.localStorage.setItem(STORAGE_KEY, nextLanguage); } catch { /* Storage may be unavailable. */ }
  }, []);
  const value = useMemo(() => ({language, setLanguage}), [language, setLanguage]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
